import { useEffect, useState } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { Shell, Head, TotalRow, SubMenu, useEsc, useRowNav, navRowCls, navRowProps, fmtV, fmtQ } from "./ReportShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Outstandings({ type, onBack }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onBack);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/reports/outstandings`, { params: { type } }).then((r) => setData(r.data)).catch(() => {});
  }, [type, dfv]);
  if (!data) return null;
  const recv = type === "receivables";
  return (
    <Shell title={recv ? "Bills Receivable (Outstandings)" : "Bills Payable (Outstandings)"} onBack={onBack}
      testid={`outstandings-${type}`}
      subtitle={<><span>{recv ? "Party-wise amounts to receive" : "Party-wise amounts to pay"}</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Party" }, { t: "Billed", w: "w-[170px]", right: true },
        { t: recv ? "Received" : "Paid", w: "w-[170px]", right: true }, { t: "Pending", w: "w-[170px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="os-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`os-row-${i}`}>
            <div className="flex-1 px-3 py-0.5 font-semibold">{r.party}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{fmtV(r.billed)}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r.settled ? fmtV(r.settled) : ""}</div>
            <div className={`w-[170px] px-2 py-0.5 text-right font-semibold ${r.pending > 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtV(r.pending)}</div>
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No bills in this period</div>}
      </div>
      <TotalRow testid="os-totals" cells={[{ t: "Grand Total" }, { t: fmtV(data.totals.billed), w: "w-[170px]", right: true },
        { t: fmtV(data.totals.settled), w: "w-[170px]", right: true }, { t: fmtV(data.totals.pending), w: "w-[170px]", right: true }]} />
    </Shell>
  );
}

export function StatementsAccountsView({ onClose }) {
  const [pick, setPick] = useState("");
  if (pick) return <Outstandings type={pick} onBack={() => setPick("")} />;
  return <SubMenu title="Statements of Accounts" onBack={onClose} onPick={setPick} testid="stmt-accounts-menu"
    items={[{ key: "receivables", label: "Outstandings — Receivables" },
      { key: "payables", label: "Outstandings — Payables" },
      { key: "quit", label: "Quit", gapBefore: true }]} />;
}

export function StatementsInventoryView({ onClose }) {
  const [data, setData] = useState(null);
  const dfv = useDateFilterVersion();
  useEsc(onClose);
  const [sel, setSel] = useRowNav(data ? data.rows.length : 0);
  useEffect(() => {
    axios.get(`${API}/reports/inventory-statement`).then((r) => setData(r.data)).catch(() => {});
  }, [dfv]);
  if (!data) return null;
  return (
    <Shell title="Statement of Inventory" onBack={onClose} testid="stmt-inventory-view"
      subtitle={<><span>Item-wise Opening · Inwards · Outwards · Closing</span><span>{data.period}</span></>}>
      <Head cols={[{ t: "Stock Item" }, { t: "Opening Qty · Value", w: "w-[170px]", right: true },
        { t: "Inwards Qty · Value", w: "w-[170px]", right: true }, { t: "Outwards Qty · Value", w: "w-[170px]", right: true },
        { t: "Closing Qty · Value", w: "w-[180px]", right: true }]} />
      <div className="flex-1 overflow-y-auto" data-testid="siv-rows">
        {data.rows.map((r, i) => (
          <div key={i} {...navRowProps(i, sel, setSel)} className={`flex items-center text-sm text-slate-900 ${navRowCls(sel === i)}`} data-testid={`siv-row-${i}`}>
            <div className="flex-1 px-2 py-0.5 font-semibold">{r.name}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r.open_q ? `${fmtQ(r.open_q)} · ${fmtV(r.open_v)}` : ""}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r.in_q ? `${fmtQ(r.in_q)} · ${fmtV(r.in_v)}` : ""}</div>
            <div className="w-[170px] px-2 py-0.5 text-right">{r.out_q ? `${fmtQ(r.out_q)} · ${fmtV(r.out_v)}` : ""}</div>
            <div className="w-[180px] px-2 py-0.5 text-right font-semibold">{fmtQ(r.close_q) || "0"} · {fmtV(r.close_v)}</div>
          </div>
        ))}
        {data.rows.length === 0 && <div className="px-4 py-6 text-center text-xs text-slate-500">No inventory records yet</div>}
      </div>
      <TotalRow testid="siv-totals" cells={[{ t: "Grand Total" },
        { t: `${fmtQ(data.grand_total.close_q)} · ${fmtV(data.grand_total.close_v)}`, w: "w-[280px]", right: true }]} />
    </Shell>
  );
}
